/**
 * Tent-level School Admin module access from the RBAC inventory.
 *
 * Tenet admins (ULA) reach every module. Program admins (PLA) see only modules
 * their module admin roles unlock — e.g. Admin - Student Compliance opens
 * Compliance Management, not Program 360 or Contract and Site.
 */

import type { SchoolAdminModule, SchoolAdminModuleId } from "@/lib/school-admin-nav"
import { moduleAdminRolesInProgram } from "@/lib/program-personas"
import {
  isTenantAdminTier,
  moduleAdminRoles,
  type ModuleAdminRole,
} from "@/lib/workspace-rbac"

/** Module admin roles that unlock each tent module (Proposed Roles for RBAC sheet). */
export const SCHOOL_ADMIN_MODULE_ROLES: Record<SchoolAdminModuleId, readonly ModuleAdminRole[]> = {
  "program-360": ["clin-ed", "user-management"],
  compliance: ["student-compliance", "faculty-compliance"],
  "contracts-sites": ["site-placement", "site-contract", "site-contract-requests"],
  "placement-report": ["clin-ed", "site-placement"],
}

function heldModuleRoles(programId: string | null): ModuleAdminRole[] {
  if (isTenantAdminTier()) {
    return moduleAdminRoles()
  }
  if (programId) {
    const scoped = moduleAdminRolesInProgram(programId)
    if (scoped.length > 0) return scoped
  }
  return moduleAdminRoles()
}

/** Whether this session may open a tent module, optionally scoped to one program. */
export function canAccessSchoolAdminModule(
  moduleId: SchoolAdminModuleId,
  programId: string | null = null,
): boolean {
  if (isTenantAdminTier()) return true
  const required = SCHOOL_ADMIN_MODULE_ROLES[moduleId]
  const held = new Set(heldModuleRoles(programId))
  return required.some(role => held.has(role))
}

export function filterSchoolAdminModules(
  modules: readonly SchoolAdminModule[],
  programId: string | null = null,
): SchoolAdminModule[] {
  if (isTenantAdminTier()) return [...modules]
  return modules.filter(module => canAccessSchoolAdminModule(module.id, programId))
}

/** Whether a tent pathname is licensed for this session. */
export function canAccessSchoolAdminPath(
  pathname: string,
  modules: readonly SchoolAdminModule[],
  programId: string | null = null,
): boolean {
  if (pathname === "/admin/placement-report") {
    return canAccessSchoolAdminModule("placement-report", programId)
  }
  for (const module of modules) {
    if (module.id === "placement-report") continue
    if (!pathname.startsWith(`${module.basePath}/`) && pathname !== module.basePath) continue
    return canAccessSchoolAdminModule(module.id, programId)
  }
  return false
}
